import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import InfoIcon from '@mui/icons-material/Info';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';

import CompositeDialog from '../dialog/compositeDialog';
import Loader from '../workflows/loader';
import RichTextEditor, { RichTextEditorRef } from './rich-text-editor';

import { Alert, Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { callsRequests } from 'redux/requests/calls.request';
import {
  actingUserAppSelectors,
  selectActingUser,
} from 'redux/slices/actingUserSlice';
import { callsAppSelectors } from 'redux/slices/callsSlice';
import { selectFeedItemByFeedItemId } from 'redux/slices/feedSlice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hooks';
import { useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { PaddedTextFieldContainer } from '../dialog/dialogStyledComponents';

import { Permission } from '@act/shared/authorization';
import { CALL_INSUFFICENT_NOTES_OUTCOMES } from '@act/shared/constants';
import WorkflowCard from 'react-components/member-feed/components/feed-card/workflow-card';
import { NeedMiniInfoBox } from 'react-components/needs/components/need-mini-info';
import useConfirmEngagementDialog from './hooks/use-confirm-engagement-dialog';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';

import { ControlledSelect, ControlledTextField } from '@act/storybook';
import {
  selectMemberById,
  selectSelectedMember,
} from 'redux/slices/memberSlice';
import { needsSelectors } from 'redux/slices/needs.slice';
import { clientAppSelectors } from 'redux/slices/clientSlice';
import { getEnvironment } from 'toolkit/environment/environment/environment';
import { DATE_TIME_FORMAT } from 'utilities/date-time-generator';

import {
  CallCreationRequest,
  CallDto,
  CallUpdateRequestDto,
  NeedDto,
  ScheduledEventDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { CommunicationTiming } from '@act/shared/types';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import { displayName } from 'utilities/formatters/display-name';
import { TrackingProvider } from '../tracking/TrackingContext';
import UnifiedGoalsContent from './components/UnifiedGoalsContent';

const AlertContainer = styled.div`
  margin-bottom: 12px;
`;

const Shim2MU = styled.div`
  margin-bottom: 16px;
`;

const Shim3MU = styled.div`
  margin-bottom: 24px;
`;

export interface PhoneCallDialogInterfaceProps {
  user: UserDto;
  call?: CallDto;
  scheduledEvent?: ScheduledEventDto;
  need?: NeedDto;
  isCustomResourcing?: boolean;
  fromResourceHub?: boolean;
  isFeedbackCall?: boolean;
  highlightedNumber?: string;
  completionCallback?: () => void;
}
interface PhoneCallDialogProps extends PhoneCallDialogInterfaceProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit';
  open: boolean;
}

export interface PhoneCallForm {
  outcomeId: number;
  note: string;
  userNeedId: number;
  needId: number;
  needCategory: number;
  needStatus: string;
  needTitle: string;
  resourceName: string;
  resourceNumber: string;
  selectedPhoneNumberId: number;
}

const PhoneCallDialog = ({
  handleClose,
  mode,
  call: existingCall,
  user,
  open,
  scheduledEvent = null,
  need,
  isCustomResourcing,
  isFeedbackCall,
  highlightedNumber,
  completionCallback,
}: PhoneCallDialogProps) => {
  const environment = getEnvironment();
  const algorithmStartDate = DateTime.fromFormat(
    environment.memberStatusAlgorithmStartDate,
    DATE_TIME_FORMAT
  );
  const needsCallFF = useFeatureFlag('needs-call');

  const [isCustomResourcingCall, setIsCustomResourcingCall] =
    useState(isCustomResourcing);

  const openConfirmEngagementDialog = useConfirmEngagementDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();

  // Track the last member we fetched guidance for to prevent duplicate requests
  const lastFetchedMemberRef = useRef<string | null>(null);

  const [isEngageEnabledWhileActive, setEngagedEnabledWhileActive] =
    useState(false);

  // Ref to access RichTextEditor methods
  const richTextEditorRef = useRef<RichTextEditorRef>(null);

  const [newCall, isCallLoading, createCall] = useApiCall(
    callsRequests.createCall
  );
  const isEditMode = mode === 'Edit' || !!newCall;

  const [updatedCall, isUpdatingCall, updateCall] = useApiCall(
    callsRequests.updateCall
  );

  // Computed loading state from API calls
  const isSaving = isCallLoading || isUpdatingCall;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isManualSave, setIsManualSave] = useState(false);
  const [pendingManualSave, setPendingManualSave] =
    useState<PhoneCallForm | null>(null);

  // Track original call data for change detection
  const originalCallDataRef = useRef<Partial<CallDto> | null>(null);

  const call = newCall || existingCall;

  const needs = needsSelectors
    .useSelectNeedsByMember(user?.id)
    .filter(need => need.fulfilledAt === null && need.completed === false)
    .sort((a, b) => a.expiresAt - b.expiresAt)
    .slice(0, 3);

  const canEditEngagement = call
    ? DateTime.fromMillis(call.timeStarted) > algorithmStartDate
    : true;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm<PhoneCallForm>({
    defaultValues: {
      note: call?.note || '',
    },
  });
  const selectedOutcomeId = watch('outcomeId');

  // Create phone number options for the dropdown
  const phoneNumberOptions = useMemo(() => {
    if (!user?.phoneNumbers) return [];

    return user.phoneNumbers.map(phn => ({
      label: `${phn.description ?? `Phone Number ${phn.id}`} - ${phn.number}`,
      value: phn.id,
      number: phn.number,
      isCallBackNumber: highlightedNumber === phn.number,
    }));
  }, [user?.phoneNumbers, highlightedNumber]);

  // Set default phone number (prefer highlighted number, otherwise first available)
  useEffect(() => {
    if (phoneNumberOptions.length > 0 && !isEditMode) {
      const defaultOption =
        phoneNumberOptions.find(option => option.isCallBackNumber) ||
        phoneNumberOptions[0];
      setValue('selectedPhoneNumberId', defaultOption.value);
    }
  }, [phoneNumberOptions, isEditMode, setValue]);

  const callFeedItemSelector = useMemo(
    () => selectFeedItemByFeedItemId(call?.id, 'call'),
    [call]
  );

  const actingUser = useAppSelector(selectActingUser);
  const callFeedItem = useAppSelector(callFeedItemSelector);
  const selectedCall = callsAppSelectors.useSelectCallById(
    callFeedItem?.externalItemId
  );
  // Always call useAppSelector hooks to follow Rules of Hooks
  const memberById = useAppSelector(selectMemberById(selectedCall?.subjectId));
  const selectedMemberFromStore = useAppSelector(selectSelectedMember);

  // Determine which member to use
  const selectedMember = selectedCall?.subjectId
    ? memberById
    : user || selectedMemberFromStore;
  const [
    communicationGuidances,
    isCommunicationGuidanceLoading,
    getCommunicationGuidances,
    communicationGuidanceError,
  ] = useApiCall(callsRequests.getCommunicationGuidances);

  // Fetch communication guidance when member or client changes
  useEffect(() => {
    // Only fetch if we have a member and haven't already fetched for this member
    if (
      selectedMember?.id &&
      lastFetchedMemberRef.current !== selectedMember.id
    ) {
      getCommunicationGuidances({
        params: {
          memberId: selectedMember.id,
          clientId: selectedMember.clientId,
          communicationTiming: CommunicationTiming.PRE,
        },
      });
    }
  }, [
    selectedMember?.id,
    selectedMember?.clientId,
    getCommunicationGuidances,
    isEditMode,
  ]);

  // Update the ref when communication guidance request completes (success or error)
  useEffect(() => {
    if (
      selectedMember?.id &&
      lastFetchedMemberRef.current !== selectedMember.id &&
      !isCommunicationGuidanceLoading // Request has completed (either success or error)
    ) {
      lastFetchedMemberRef.current = selectedMember.id;
    }
  }, [selectedMember?.id, isCommunicationGuidanceLoading]);

  const callEngagementLengthRequirement =
    clientAppSelectors.useSelectClientById(
      selectedMember?.clientId
    )?.callEngagementLengthRequirementInMinutes;

  const isResourcing =
    !!call?.resourceInteraction ||
    !!call?.needResourceInteraction ||
    isCustomResourcingCall;
  const latestCall = selectedCall || call;

  const targetOutcomeType =
    isResourcing || call?.needResourceInteraction
      ? 'resourcing'
      : 'interaction';

  const canOverrideUnderTwoMinuteEngagement =
    actingUserAppSelectors.useUserHasPermission(
      Permission.CAN_OVERRIDE_UNDER_TWO_MINUTE_ENGAGEMENT
    );

  const includeEngagedStatus: boolean = useMemo(() => {
    if (canOverrideUnderTwoMinuteEngagement) {
      return true;
    }
    if (latestCall?.timeConnected && latestCall?.timeEnded) {
      const ended = DateTime.fromMillis(latestCall?.timeEnded);
      const connected = DateTime.fromMillis(latestCall?.timeConnected);
      if (
        ended.diff(connected, 'minutes').minutes >=
        callEngagementLengthRequirement
      ) {
        return true;
      }
    }
    if (isEngageEnabledWhileActive) {
      return true;
    }
    return false;
  }, [actingUser, latestCall, isEngageEnabledWhileActive]);

  const outcomes = metaSliceSelectors.useSelectCallOutcomes();
  const outcomeOptions = metaSliceSelectors.useSelectCallOutcomeOptions(
    targetOutcomeType,
    includeEngagedStatus,
    call?.timeStarted
  );

  const isFromResourceHub = !!call?.resourceInteraction?.resource?.findHelpId;

  // Determine if this is a need-based resourcing call vs resource hub call
  const isNeedBasedResourcing = isResourcing && need && !isFromResourceHub;

  useEffect(() => {
    let timer;

    if (latestCall?.timeConnected && !latestCall?.timeEnded) {
      const connectedAt = DateTime.fromMillis(latestCall?.timeConnected);
      const twoMinutesPassedAt = connectedAt.plus({ minutes: 2 });
      const difference = twoMinutesPassedAt.diff(
        DateTime.now(),
        'milliseconds'
      ).milliseconds;

      if (difference <= 0) {
        setEngagedEnabledWhileActive(true);
        return;
      }

      timer = setTimeout(() => {
        setEngagedEnabledWhileActive(true);
      }, difference);
    }

    return () => clearTimeout(timer);
  }, [latestCall]);

  const outcomeHasCharacterMinimum: boolean = useMemo(() => {
    if (!outcomeOptions || !selectedOutcomeId) {
      return false;
    }
    const outcome = outcomes.find(o => o.id === selectedOutcomeId);
    return CALL_INSUFFICENT_NOTES_OUTCOMES.includes(outcome.key);
  }, [outcomeOptions, selectedOutcomeId]);

  useEffect(() => {
    if (call) {
      setValue('outcomeId', call.outcomeId);
      setValue('note', call.note);
      if (call.resourceInteraction) {
        setValue('resourceName', call.resourceInteraction.resource.title);
      }
      if (call.needResourceInteraction) {
        setValue('resourceName', call.needResourceInteraction.resource.title);
      }

      // Store original call data for change detection
      originalCallDataRef.current = {
        outcomeId: call.outcomeId,
        note: call.note,
        resourceInteraction: call.resourceInteraction,
        needResourceInteraction: call.needResourceInteraction,
      };
    }
  }, [call]);

  // Helper function to detect if form data has changed
  const hasFormDataChanged = useCallback(
    (formData: PhoneCallForm): boolean => {
      if (!originalCallDataRef.current) {
        return true; // If no original data, consider it changed
      }

      const original = originalCallDataRef.current;

      // Check if outcome has changed
      if (formData.outcomeId !== original.outcomeId) {
        return true;
      }

      // Check if note has changed (RichTextEditor handles this, but we check here for manual saves)
      if (formData.note !== original.note) {
        return true;
      }

      // Check if resource name has changed
      if (isResourcing) {
        const originalResourceName =
          original.resourceInteraction?.resource?.title ||
          original.needResourceInteraction?.resource?.title;
        if (formData.resourceName !== originalResourceName) {
          return true;
        }
      }

      return false;
    },
    [isResourcing]
  );

  function getDialogActionText(): string {
    if (call?.isFeedbackCall) {
      return 'Edit Feedback';
    } else {
      return isEditMode ? 'Edit' : 'Create';
    }
  }

  async function startCall(callDetails: CallCreationRequest) {
    // Prevent multiple simultaneous calls
    if (isSaving) {
      return;
    }

    createCall({
      params: null,
      body: {
        targetUserId: user?.id,
        phoneNumber: callDetails.phoneNumber,
        isFeedbackCall: isFeedbackCall,
        ...(need && { userNeedId: need.id }),
        ...(callDetails?.phoneNumberId && {
          phoneNumberId: callDetails?.phoneNumberId,
        }),
        ...(scheduledEvent?.id && { scheduledEventId: scheduledEvent?.id }),
        ...(need && { needId: need.id }),
        ...(callDetails?.resourceInteraction &&
          !need && {
            resourceInteraction: {
              title: callDetails.resourceInteraction.title,
              phoneNumber: callDetails.phoneNumber,
            },
          }),
        ...(callDetails?.resourceInteraction &&
          !!need && {
            needResourceInteraction: {
              title: callDetails.resourceInteraction.title,
              phoneNumber: callDetails.phoneNumber,
              userNeedId: need.id,
            },
          }),
      },
    });
  }

  // Handle API call states
  useEffect(() => {
    // Handle successful call updates
    if (updatedCall && isManualSave) {
      // Call completion callback if provided
      if (completionCallback) {
        completionCallback();
      }
      // Always close dialog on successful manual save
      reset();
      handleClose();
      setIsManualSave(false); // Reset the flag
      setPendingManualSave(null); // Clear any pending manual save
    } else if (updatedCall && !isManualSave) {
      // Auto-save completed
    }
  }, [updatedCall, isManualSave, completionCallback, handleClose]);

  // Unified save function that handles both auto-save and manual save
  const performSave = useCallback(
    async (data: PhoneCallForm, isManualSaveParam: boolean = false) => {
      // Prevent multiple simultaneous saves
      if (isSaving) {
        return;
      }

      setSaveError(null);

      // Set the manual save flag if this is a manual save
      if (isManualSaveParam) {
        setIsManualSave(true);
      }

      try {
        // Cancel any pending autosave if this is a manual save
        if (isManualSaveParam && richTextEditorRef.current) {
          richTextEditorRef.current.cancelPendingAutoSave();
        }

        const selectedOutcome = outcomes.find(
          outcome => outcome.id === data.outcomeId
        );

        // Show confirmation dialog for manual saves with engagement outcome
        if (
          isManualSaveParam &&
          user?.memberStage.key === 'outreach' &&
          selectedOutcome?.key === 'engaged'
        ) {
          const confirmed = await openConfirmEngagementDialog();
          if (!confirmed) {
            setIsManualSave(false); // Reset the flag
            return;
          }
        }

        const params = { id: call.id };
        const body: CallUpdateRequestDto = {
          note: data.note,
        };

        // Only include additional fields for manual saves (not autosave)
        if (isManualSaveParam) {
          body.outcomeId = data.outcomeId;

          // Include resourcing fields for manual saves
          if (isResourcing) {
            if (isNeedBasedResourcing) {
              // Use needResourceInteraction for need-based resourcing calls
              body.needResourceInteraction = {
                title: data.resourceName,
                userNeedId: need?.id,
              };
            } else if (isFromResourceHub) {
              // Use resourceInteraction for Resource Hub calls
              body.resourceInteraction = {
                title: call?.resourceInteraction?.resource?.title,
                findHelpId: call?.resourceInteraction?.resource?.findHelpId,
              };
            }
          }
        }

        await updateCall({ params, body });

        // Update original data reference after successful save
        if (originalCallDataRef.current) {
          originalCallDataRef.current = {
            ...originalCallDataRef.current,
            note: data.note,
            // Only update other fields for manual saves
            ...(isManualSaveParam && {
              outcomeId: data.outcomeId,
              ...(isResourcing && {
                resourceInteraction: isNeedBasedResourcing
                  ? undefined
                  : call.resourceInteraction,
                needResourceInteraction: isNeedBasedResourcing
                  ? {
                      ...call.needResourceInteraction,
                      resource: {
                        ...call?.needResourceInteraction?.resource,
                        title: data.resourceName,
                      },
                    }
                  : undefined,
              }),
            }),
          };
        }

        // Only mark as saved for manual saves
        if (isManualSaveParam) {
          if (richTextEditorRef.current) {
            richTextEditorRef.current.markAsSaved(data.note);
          }
        }
      } catch (err) {
        console.error(
          `Error ${isManualSaveParam ? 'saving' : 'auto-saving'} call:`,
          err
        );
        setSaveError(err instanceof Error ? err.message : 'Save failed');
        setIsManualSave(false); // Reset the flag on error
      }
    },
    [
      isSaving,
      getValues,
      outcomes,
      user,
      openConfirmEngagementDialog,
      call,
      isResourcing,
      isNeedBasedResourcing,
      isFromResourceHub,
      need,
      updateCall,
    ]
  );

  // Handle pending manual save after autosave completes
  useEffect(() => {
    if (!isSaving && pendingManualSave && !isManualSave) {
      // Autosave has completed, now execute the pending manual save
      void performSave(pendingManualSave, true);
    }
  }, [isSaving, pendingManualSave, isManualSave, performSave]);

  // Manual save handler - only saves if data has changed since last save
  const handleSave = useCallback(
    async (data?: PhoneCallForm) => {
      const formData = data || getValues();

      // Get the current value from the RichTextEditor to ensure we have the latest
      if (richTextEditorRef.current && !data) {
        const currentNoteValue = richTextEditorRef.current.getCurrentValue();
        formData.note = currentNoteValue;
      }

      // Check if anything has actually changed
      if (!hasFormDataChanged(formData)) {
        // No changes detected, just close the dialog
        reset();
        handleClose();
        return;
      }

      // If we're currently saving (autosave in progress), queue the manual save
      if (isSaving) {
        setPendingManualSave(formData);
        return;
      }

      await performSave(formData, true);
    },
    [getValues, performSave, isSaving, hasFormDataChanged, reset, handleClose]
  );

  // Auto-save handler - only saves RichTextEditor content when it changes
  function handleAutoSave(autoSaveNote: string) {
    // Only autosave if we have a valid call ID (existing call)
    if (!call?.id) {
      return;
    }

    // Don't autosave if we're already saving or if there's a pending manual save
    if (isSaving || pendingManualSave) {
      return;
    }

    // Only autosave if the note has actually changed
    const originalNote = originalCallDataRef.current?.note || '';
    if (autoSaveNote === originalNote) {
      return;
    }

    // Create minimal form data for autosave (only note)
    const autoSaveData: PhoneCallForm = {
      ...getValues(),
      note: autoSaveNote,
    };

    void performSave(autoSaveData, false);
  }

  const calledEnded = !!latestCall?.timeEnded;

  // Handle close with unsaved changes check
  const handleCloseWithUnsavedCheck = useCallback(() => {
    // Only check for unsaved changes in edit mode
    if (!isEditMode) {
      handleClose();
      return;
    }

    const formData = getValues();

    // Get the current value from the RichTextEditor to ensure we have the latest
    if (richTextEditorRef.current) {
      richTextEditorRef.current.cancelPendingAutoSave();
      const currentNoteValue = richTextEditorRef.current.getCurrentValue();
      formData.note = currentNoteValue;
    }

    // Check if anything has actually changed
    if (!hasFormDataChanged(formData)) {
      // No changes detected, just close the dialog
      handleClose();
      return;
    }

    // Show unsaved changes dialog
    void handleUnsavedChanges(() => {
      handleClose();
    });
  }, [
    isEditMode,
    getValues,
    hasFormDataChanged,
    handleUnsavedChanges,
    handleClose,
  ]);

  return (
    <TrackingProvider contextName="phone-call-dialog">
      <CompositeDialog
        handleClose={handleCloseWithUnsavedCheck}
        maxWidth="md"
        open={open}
        icon={<PhoneForwardedIcon color="primary" sx={{ marginRight: 1 }} />}
        title={`${getDialogActionText()} call for ${displayName(user, {
          onlyLastInitial: true,
        })}`}
        handleFormSubmit={handleSubmit(data => {
          void handleSave(data);
        })}
        actions={
          isEditMode
            ? [
                {
                  text: 'Cancel',
                  variant: 'outlined',
                  callback: () => handleCloseWithUnsavedCheck(),
                },
                {
                  text: isSaving ? 'Saving...' : 'Save',
                  type: 'submit',
                  disabled: isSaving,
                },
              ]
            : isCustomResourcingCall
            ? [
                {
                  text: 'Cancel',
                  variant: 'outlined',
                  callback: () => handleCloseWithUnsavedCheck(),
                },
                {
                  text: isSaving ? 'Calling...' : 'Call',
                  isPrimary: true,
                  disabled:
                    !getValues('resourceNumber') || !isValid || isSaving,
                  callback: () => {
                    const { resourceName, resourceNumber } = getValues();
                    void startCall({
                      phoneNumber: resourceNumber,
                      resourceInteraction: {
                        title: resourceName,
                        phoneNumber: `+${resourceNumber}`,
                      },
                    });
                  },
                },
              ]
            : [
                {
                  text: 'Resourcing Call',
                  isLeft: true,
                  callback: () => setIsCustomResourcingCall(true),
                },
                {
                  text: 'Cancel',
                  variant: 'outlined',
                  callback: () => handleCloseWithUnsavedCheck(),
                },
                {
                  text: isSaving ? 'Calling...' : 'Call',
                  isPrimary: true,
                  disabled: !getValues('selectedPhoneNumberId') || isSaving,
                  callback: () => {
                    {
                      const selectedPhoneNumber = phoneNumberOptions.find(
                        option =>
                          option.value === getValues('selectedPhoneNumberId')
                      );
                      if (selectedPhoneNumber) {
                        void startCall({
                          phoneNumber: selectedPhoneNumber.number,
                          phoneNumberId: selectedPhoneNumber.value,
                        });
                      }
                    }
                  },
                },
              ]
        }
      >
        {isCallLoading && <Loader />}
        {!isCallLoading && !isEditMode && !isCustomResourcingCall && (
          <React.Fragment>
            <Typography variant="titleMedium">
              Select a phone number to call
            </Typography>
            <Box sx={{ margin: '16px 0 16px' }}>
              <ControlledSelect
                control={control}
                errors={errors}
                label={''}
                name={'selectedPhoneNumberId'}
                options={phoneNumberOptions}
                trackingName="phone-number-select"
                fullWidth
                required
              />
            </Box>

            <UnifiedGoalsContent
              needs={needs}
              user={user}
              callEnded={calledEnded}
              communicationGuidances={communicationGuidances ?? []}
              isLoading={isCommunicationGuidanceLoading}
              error={communicationGuidanceError?.message}
            />
          </React.Fragment>
        )}
        {!isCallLoading && !isEditMode && isCustomResourcingCall && (
          <React.Fragment>
            {need && <NeedMiniInfoBox need={need}></NeedMiniInfoBox>}
            <ControlledTextField
              defaultValue={''}
              errors={errors}
              label={'Resource name'}
              name={'resourceName'}
              trackingName="resource-name-input"
              control={control}
              required
            />
            <Shim3MU />
            <PaddedTextFieldContainer>
              <ControlledTextField
                label={'Phone Number'}
                name={'resourceNumber'}
                trackingName="resource-number-input"
                control={control}
                errors={errors}
                isPhoneNumber
              />
            </PaddedTextFieldContainer>
          </React.Fragment>
        )}
        {!isCallLoading && isEditMode && (
          <React.Fragment>
            {saveError && (
              <AlertContainer>
                <Alert severity="error">
                  <Typography variant="bodySmall">{saveError}</Typography>
                </Alert>
              </AlertContainer>
            )}
            {isEditMode && (
              <React.Fragment>
                {((isResourcing && !isFromResourceHub) ||
                  !!call?.resourceInteraction?.resource?.title) && (
                  <React.Fragment>
                    <ControlledTextField
                      defaultValue={''}
                      errors={errors}
                      label={'Resource Name'}
                      name={'resourceName'}
                      trackingName="resource-name-input"
                      control={control}
                      required
                    />
                    <Shim2MU />
                  </React.Fragment>
                )}
                {need && <NeedMiniInfoBox need={need}></NeedMiniInfoBox>}
                {!call?.isFeedbackCall && (
                  <Box sx={{ margin: '8px 0 16px' }}>
                    <ControlledSelect
                      control={control}
                      errors={errors}
                      label={'Outcome'}
                      name={'outcomeId'}
                      options={outcomeOptions}
                      fullWidth
                      disabled={!canEditEngagement}
                      trackingName="outcome-select"
                      sensitive={false}
                    />
                  </Box>
                )}
                {outcomeHasCharacterMinimum && (
                  <AlertContainer>
                    <Alert
                      severity="info"
                      icon={<InfoIcon fontSize="inherit" />}
                    >
                      <Typography variant="bodySmall">
                        For this outcome a note of 50 characters or more is
                        required.
                      </Typography>
                    </Alert>
                  </AlertContainer>
                )}
                <UnifiedGoalsContent
                  needs={needs}
                  user={user}
                  callEnded={calledEnded}
                  communicationGuidances={communicationGuidances ?? []}
                  isLoading={isCommunicationGuidanceLoading}
                  error={communicationGuidanceError?.message}
                />
                <RichTextEditor
                  control={control}
                  defaultValue={call?.note || ''}
                  name={'note'}
                  autoSaveFn={handleAutoSave}
                  showCharacterCount={true}
                  ref={richTextEditorRef}
                />
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        {scheduledEvent?.assignedWorkflowScheduledEvents?.map(
          assignedWorkflowScheduledEvent => {
            return (
              <WorkflowCard
                gettingStarted={true}
                key={`assigned-workflow-phase-${assignedWorkflowScheduledEvent.id}`}
                assignedWorkflowPhaseId={
                  assignedWorkflowScheduledEvent.assignedWorkflowPhaseId
                }
                assignedWorkflowScheduledEventId={
                  assignedWorkflowScheduledEvent.id
                }
              />
            );
          }
        )}
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default PhoneCallDialog;
